import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail, Book, HelpCircle, FileText } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Support Center - Get Help with Seoro | Documentation & Guides"
        description="Get help with Seoro. Find answers to common questions, access comprehensive documentation, step-by-step guides, and contact our support team. We respond within 24 hours."
        keywords="Seoro support, SEO platform help, content automation support, documentation, guides, customer support, FAQ"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Support Center
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're here to help. Find answers to common questions or get in touch with our team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => navigate('/docs')}
              className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-all hover:shadow-lg group text-left"
            >
              <Book className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-foreground mb-2">Documentation</h3>
              <p className="text-muted-foreground">
                Complete guides covering getting started, content generation, backlinks, and LLMs.txt. Everything you need to use Seoro effectively.
              </p>
            </button>

            <button
              onClick={() => navigate('/guides')}
              className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-all hover:shadow-lg group text-left"
            >
              <HelpCircle className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-foreground mb-2">Step-by-Step Guides</h3>
              <p className="text-muted-foreground">
                Detailed tutorials for common tasks: setting up your account, generating articles, claiming backlinks, and more.
              </p>
            </button>

            <button
              onClick={() => navigate('/contact')}
              className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-all hover:shadow-lg group text-left"
            >
              <Mail className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-foreground mb-2">Email Support</h3>
              <p className="text-muted-foreground">
                Need help? Send us an email and we'll get back to you within 24 hours with a solution.
              </p>
            </button>

            <button
              onClick={() => navigate('/integrations')}
              className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-all hover:shadow-lg group text-left"
            >
              <FileText className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-foreground mb-2">Integrations</h3>
              <p className="text-muted-foreground">
                Learn how to connect Seoro with Shopify, WordPress, and Webflow for one-click publishing. Framer integration coming soon!
              </p>
            </button>
          </div>

          <div className="bg-muted/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">How do I get started?</h3>
                <p className="text-muted-foreground">
                  Sign up with Google, connect your website, and start generating content. 
                  Our onboarding flow will guide you through the setup process.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">What CMS platforms do you support?</h3>
                <p className="text-muted-foreground">
                  We currently support Shopify, WordPress, and Webflow. Framer integration is coming soon. More integrations are in development. Check out our 
                  integration documentation for the latest updates.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Can I cancel my subscription anytime?</h3>
                <p className="text-muted-foreground">
                  Yes, you can cancel your subscription at any time. You'll continue to 
                  have access until the end of your billing period.
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                // Calculate header height dynamically
                const getHeaderHeight = () => {
                  const header = document.querySelector('header');
                  return header ? header.offsetHeight + 20 : 100; // Add 20px padding
                };

                if (location.pathname !== '/') {
                  navigate('/');
                  // Wait for navigation, then scroll
                  setTimeout(() => {
                    const scrollToFAQ = () => {
                      const element = document.getElementById('faq');
                      if (element) {
                        const headerOffset = getHeaderHeight();
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      } else {
                        // Retry if element not found yet
                        setTimeout(scrollToFAQ, 100);
                      }
                    };
                    scrollToFAQ();
                  }, 200);
                } else {
                  // Already on home page, just scroll
                  const element = document.getElementById('faq');
                  if (element) {
                    const headerOffset = getHeaderHeight();
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }
              }}
              variant="outline"
              className="mt-6 rounded-full"
            >
              View All FAQs
            </Button>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Support;


